import { useState } from "react";
import { Download, FileText, FileSpreadsheet, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";

/**
 * ExportDropdown — CSV + PDF export buttons for reports and jobs list.
 * Props:
 *   basePath: e.g. "/reports/engineers"  (endpoint prefix; ".csv" / ".pdf" appended)
 *   params: object to pass as query params (merged into both requests)
 *   filename: base filename (without extension) for the download
 *   testId
 */
export default function ExportDropdown({ basePath, params = {}, filename = "export", testId = "export" }) {
  const [busy, setBusy] = useState(null);

  const download = async (format) => {
    if (busy) return;
    setBusy(format);
    const dismiss = toast.loading(`Exporting ${format.toUpperCase()}…`);
    try {
      const res = await api.get(`${basePath}.${format}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`, { id: dismiss });
    } catch (err) {
      toast.error(`Export failed: ${err?.response?.status || err.message}`, { id: dismiss });
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs" data-testid={`${testId}-trigger`} disabled={!!busy}>
          <Download className="h-3.5 w-3.5 mr-1" /> {busy ? `Exporting ${busy.toUpperCase()}…` : "Export"}
          <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => download("csv")} data-testid={`${testId}-csv`}>
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Export CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => download("pdf")} data-testid={`${testId}-pdf`}>
          <FileText className="h-4 w-4 mr-2" /> Export PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
